import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { FormField } from '@/components/forms/form-field'
import { Input } from '@/components/forms/input'
import { Select } from '@/components/forms/select'
import { Drawer } from '@/components/overlays/drawer'
import { Button } from '@/components/primitives/button'

import type { OAuthResource, OAuthResourcePayload, OidcClientScope } from './api'
import { isInsecureHttpResourceUri } from './idp-oauth-resource-uri'

export function OAuthResourceFormDrawer({
  isSaving,
  onOpenChange,
  onSave,
  open,
  resource,
  scopes,
}: {
  isSaving: boolean
  onOpenChange: (open: boolean) => void
  onSave: (payload: OAuthResourcePayload) => void
  open: boolean
  resource?: OAuthResource | null
  scopes: OidcClientScope[]
}) {
  const { t } = useTranslation('adminIdp')
  const [values, setValues] = useState<OAuthResourcePayload>({
    name: resource?.name ?? '',
    resource_uri: resource?.resource_uri ?? '',
    status: resource?.status ?? 'active',
    allowed_client_scope_ids: resource?.allowed_client_scope_ids ?? [],
  })
  const enabledScopes = scopes.filter((scope) => scope.enabled)
  const valid = Boolean(
    values.name.trim() && values.resource_uri.trim() && values.allowed_client_scope_ids.length,
  )
  const insecureHttp = isInsecureHttpResourceUri(values.resource_uri)

  return (
    <Drawer
      footer={
        <div className="flex justify-end gap-3">
          <Button onClick={() => onOpenChange(false)} variant="secondary">
            {t('actions.cancel')}
          </Button>
          <Button
            disabled={!valid}
            isLoading={isSaving}
            onClick={() =>
              onSave({
                ...values,
                name: values.name.trim(),
                resource_uri: values.resource_uri.trim(),
              })
            }
          >
            {resource ? t('actions.save') : t('actions.create')}
          </Button>
        </div>
      }
      onOpenChange={onOpenChange}
      open={open}
      size="lg"
      title={resource ? t('resources.editTitle', { name: resource.name }) : t('resources.create')}
    >
      <div className="grid gap-6">
        <div className="rounded-xl border border-[var(--color-panel-border)] bg-[var(--color-surface-muted)] px-4 py-3 text-sm leading-6 text-[var(--color-text-muted)]">
          {t('resources.formHint')}
        </div>
        <FormField
          hint={t('resources.fields.nameHint')}
          htmlFor="oauth-resource-name"
          label={t('resources.fields.name')}
          required
        >
          <Input
            autoFocus
            id="oauth-resource-name"
            onChange={(event) => setValues({ ...values, name: event.target.value })}
            placeholder={t('resources.placeholders.name')}
            value={values.name}
          />
        </FormField>
        <FormField
          hint={t('resources.fields.resourceUriHint')}
          htmlFor="oauth-resource-uri"
          label={t('resources.fields.resourceUri')}
          required
        >
          <Input
            id="oauth-resource-uri"
            onChange={(event) => setValues({ ...values, resource_uri: event.target.value })}
            placeholder={t('resources.placeholders.resourceUri')}
            value={values.resource_uri}
          />
        </FormField>
        {insecureHttp ? (
          <div
            className="rounded-xl border border-[#f0c36d] bg-[#fff8e8] px-4 py-3 text-sm leading-6 text-[#8a5200]"
            role="alert"
          >
            {t('resources.insecureHttpWarning')}
          </div>
        ) : null}
        <FormField
          hint={t('resources.fields.statusHint')}
          htmlFor="oauth-resource-status"
          label={t('resources.fields.status')}
        >
          <Select
            id="oauth-resource-status"
            onValueChange={(value) =>
              setValues({ ...values, status: value as OAuthResourcePayload['status'] })
            }
            options={[
              { label: t('status.enabled'), value: 'active' },
              { label: t('status.disabled'), value: 'inactive' },
            ]}
            value={values.status}
          />
        </FormField>
        <FormField
          hint={t('resources.fields.allowedScopesHint')}
          htmlFor="oauth-resource-allowed-scopes"
          label={t('resources.fields.allowedScopes')}
          required
        >
          <div
            className="grid overflow-hidden rounded-xl border border-[var(--color-control-border)]"
            id="oauth-resource-allowed-scopes"
          >
            {enabledScopes.length ? (
              enabledScopes.map((scope) => (
                <label
                  className="flex cursor-pointer items-start gap-3 border-b border-[var(--color-divider)] px-4 py-3 text-sm last:border-b-0 hover:bg-[var(--color-hover-muted)]"
                  key={scope.id}
                >
                  <input
                    checked={values.allowed_client_scope_ids.includes(scope.id)}
                    className="mt-0.5 size-4 accent-[var(--color-primary)]"
                    onChange={(event) => {
                      const selected = new Set(values.allowed_client_scope_ids)
                      if (event.target.checked) selected.add(scope.id)
                      else selected.delete(scope.id)
                      setValues({ ...values, allowed_client_scope_ids: [...selected] })
                    }}
                    type="checkbox"
                  />
                  <span>
                    <span className="font-medium">{scope.display_name}</span>
                    <code className="ml-2 text-xs text-[var(--color-text-muted)]">
                      {scope.name}
                    </code>
                    <span className="mt-1 block text-xs leading-5 text-[var(--color-text-muted)]">
                      {scope.description || t('resources.fields.scopeFallbackDescription')}
                    </span>
                  </span>
                </label>
              ))
            ) : (
              <p className="text-sm text-[var(--color-text-muted)]">
                {t('resources.fields.noAvailableScopes')}
              </p>
            )}
          </div>
        </FormField>
      </div>
    </Drawer>
  )
}
